import produce from 'immer';
import React, {useCallback, useMemo, useRef} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {useIsAnalysisLayoutCreationEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import getCopyViewAction from 'analysis-tool/data-view/analysis-adapter/getCopyViewAction';
import getDeleteViewAction from 'analysis-tool/data-view/analysis-adapter/getDeleteViewAction';
import getEditViewAction from 'analysis-tool/data-view/analysis-adapter/getEditViewAction';
import {isLayoutView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {
  ButtonDataViewActionInterface,
  isDataViewActionConstantType,
  OverflowDataViewActionInterface,
} from 'analysis-tool/data-view/DataViewContext';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import useRightSidebarItem from 'analysis-tool/ui/hooks/useRightSidebarItem';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExperimentOptimizationFeedbackToolbar from 'machine-learning/experiment-optimization/ExperimentOptimizationFeedbackToolbar';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {
  assertPipelineStepConfigNonNull,
  assertPipelineStepIsStepType,
  PipelineViewGraphQL,
} from 'pipelines/PipelineTypes';
import {getDefaultClientIdForView} from 'pipelines/PipelineViewUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import {confirm} from 'util/Modals';
import {assertNever, JSONValue} from 'util/Types';

import {AnalysisLayoutStatus, LayoutStep} from './analysis-adapter/AnalysisAdapterTypes';
import getAnalysisLayoutStatusInfo from './analysis-adapter/getAnalysisLayoutStatusInfo';
import useHandleAnalysisLayoutEvents from './analysis-adapter/useHandleAnalysisLayoutEvents';
import DataLayout from './DataLayout';
import type {AdditionalBlockActions} from './DataLayoutContext';
import type {
  AddBlockEvent,
  Block,
  ConfigFormFieldEvent,
  DataLayout as DataLayoutType,
  GridSection as GridSectionType,
  LayoutEvent,
  MoveBlockEvent,
  RemoveBlockEvent,
} from './DataLayoutTypes';
import FailedLayout from './FailedLayout';
import LayoutSettingsModal from './layout-settings/LayoutSettingsModal';
import LayoutViewPicker from './LayoutViewPicker';
import {
  buildAddViewEdge,
  convertEdgeToUpsert,
  filterEdgesToRemainingRefs,
  getInputDataNameForView,
  getNextInputDataIndex,
  normalizeEdgeIndices,
  replaceBlockInConfig,
} from './LayoutViewUtils';
import RunningLayout from './RunningLayout';
import {isLockedLayout} from './utils/LayoutCreationUtils';
import {getAtLayoutPath, setAtLayoutPath} from './utils/LayoutPathUtils';
import {getLayoutDimensions, resizeLayoutGrid} from './utils/LayoutSettingsUtils';
import swapBlocks from './utils/swapBlocks';

export type AnalysisDataLayoutAdapterProps = MainViewComponentProps<AnalysisMainViewItemType.DATA_LAYOUT>;

/**
 * Adapter between an analysis layout step and the actual data layout component.
 *
 * This component is responsible for handling analysis-specific concerns, such
 * as the status of the layout step.
 */
export default function AnalysisDataLayoutAdapter(props: AnalysisDataLayoutAdapterProps): JSX.Element {
  const layoutStep = getStepFromAnalysis(props.analysis, props.step.id);
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);

  const statusInfo = getAnalysisLayoutStatusInfo(props.analysis, layoutStep);

  if (statusInfo.status === AnalysisLayoutStatus.RUNNING) {
    return <RunningLayout message={statusInfo.message} />;
  } else if (statusInfo.status === AnalysisLayoutStatus.FAILED) {
    return <FailedLayout message={statusInfo.message} reason={statusInfo.reason} />;
  } else if (statusInfo.status === AnalysisLayoutStatus.READY) {
    return <ReadyAnalysisDataLayoutAdapter key={layoutStep.id} {...props} layoutStep={layoutStep} />;
  } else {
    throw new Error(`Unknown layout status: ${assertNever(statusInfo)}`);
  }
}

async function openLayoutSettingsAndSave(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: LayoutStep,
  layout: DataLayoutType
): Promise<void> {
  const dimensions = getLayoutDimensions(layout.sections);
  const result = await runModal(LayoutSettingsModal, {
    currentName: layout.name,
    currentRows: dimensions.rows,
    currentColumns: dimensions.columns,
  });
  if (result == null) {
    return;
  }
  assertPipelineStepConfigNonNull(layoutStep);
  // resizeLayoutGrid expects the frontend DataLayout['sections'] type (which has `view`),
  // but layoutStep.config.sections is the backend type (which has `inputReference`). The resize
  // logic only reads structural fields (type, rows) so the cast is safe.
  const {config, displacedBlockCount} = resizeLayoutGrid(
    layoutStep.config.sections as unknown as DataLayoutType['sections'],
    result.name,
    result.rows,
    result.columns
  );
  if (displacedBlockCount > 0) {
    const confirmed = await confirm(
      'Resize layout',
      `Resizing the grid will remove ${displacedBlockCount} data ${displacedBlockCount === 1 ? 'view' : 'views'} from the layout.`,
      ButtonStyle.NEGATIVE,
      {acceptText: 'Continue', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
    );
    if (!confirmed) {
      return;
    }
  }
  const updatedConfig = {...layoutStep.config, ...config};

  // When blocks are displaced, remove edges that no longer correspond to any DATA block.
  const stepUpdate: {
    stepId: string;
    config: Record<string, JSONValue>;
    incomingEdges?: Array<ReturnType<typeof convertEdgeToUpsert>>;
  } = {
    stepId: layoutStep.id,
    config: updatedConfig as Record<string, JSONValue>,
  };
  if (displacedBlockCount > 0) {
    stepUpdate.incomingEdges = filterEdgesToRemainingRefs(layoutStep.incomingEdges, config.sections);
  }

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [stepUpdate],
  });
}

function getLayoutView(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: LayoutStep
): PipelineViewGraphQL | undefined {
  return analysis.views.find(
    (view) =>
      isLayoutView(view, analysis.steps) && view.lineage[view.lineage.length - 1].step.id === layoutStep.id
  );
}

function toOverflowMenuOption(action: OverflowDataViewActionInterface): OverflowMenuOption {
  return {
    icon: action.icon,
    contents: <span>{action.label}</span>,
    isDisabled: action.isDisabled,
    tooltipTitle: action.helpText,
    onSelect: action.onClick,
  };
}

function getLayoutOverflowMenuOptions({
  analysis,
  analysisClientStateId,
  layoutStep,
  layout,
  isLayoutCreationEnabled,
}: {
  analysis: AnalysisDataLayoutAdapterProps['analysis'];
  analysisClientStateId: string;
  layoutStep: LayoutStep;
  layout: DataLayoutType;
  isLayoutCreationEnabled: boolean;
}): Array<OverflowMenuOption> {
  assertPipelineStepConfigNonNull(layoutStep);
  if (isAnalysisReadOnly(analysis) || isLockedLayout(layoutStep.config)) {
    return [];
  }

  const layoutView = getLayoutView(analysis, layoutStep);
  const options: Array<OverflowMenuOption> = [];

  if (isLayoutCreationEnabled) {
    options.push({
      icon: 'gear',
      contents: <span>Settings</span>,
      onSelect: () => openLayoutSettingsAndSave(analysis, layoutStep, layout),
    });

    if (layoutView != null) {
      const copyAction = getCopyViewAction(analysis, layoutView);
      if (copyAction != null && typeof copyAction !== 'function') {
        options.push(toOverflowMenuOption(copyAction));
      }
    }
  }

  if (layoutView != null) {
    const deleteAction = getDeleteViewAction(analysis, layoutView, analysisClientStateId);
    if (deleteAction != null && typeof deleteAction !== 'function') {
      options.push(toOverflowMenuOption(deleteAction));
    }
  }

  return options;
}

async function handleAddBlock(
  event: AddBlockEvent,
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: LayoutStep
): Promise<void> {
  const selectedView = await runModal(LayoutViewPicker, {analysis});
  if (selectedView == null) {
    return;
  }

  const inputDataName = getInputDataNameForView(selectedView);
  const newEdge = buildAddViewEdge(selectedView, inputDataName, 0);
  if (newEdge == null) {
    return;
  }
  const existingEdges = layoutStep.incomingEdges.map((edge) => convertEdgeToUpsert(edge));

  // If the view already has an edge into the layout, reuse its index for the new DATA block
  // instead of creating a duplicate edge (which would cause a SQLAlchemy identity map conflict).
  const existingEdge = existingEdges.find(
    (edge) =>
      edge.fromStepId === newEdge.fromStepId &&
      edge.fromStepDataName === newEdge.fromStepDataName &&
      edge.fromStepDataIndex === newEdge.fromStepDataIndex
  );

  let allEdges: typeof existingEdges;
  let inputDataIndex: number;
  if (existingEdge != null) {
    allEdges = existingEdges;
    inputDataIndex = existingEdge.toStepDataIndex!;
  } else {
    inputDataIndex = getNextInputDataIndex(layoutStep, inputDataName);
    const edgeWithIndex = {...newEdge, toStepDataIndex: inputDataIndex};
    allEdges = normalizeEdgeIndices([...existingEdges, edgeWithIndex]);
  }

  assertPipelineStepConfigNonNull(layoutStep);
  const newConfig = replaceBlockInConfig(layoutStep.config, event.layoutPath, {
    type: 'DATA',
    inputReference: {inputDataName, inputDataIndex},
  });

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [
      {
        stepId: layoutStep.id,
        incomingEdges: allEdges,
        config: newConfig as Record<string, JSONValue>,
        rerunAfterUpdate: true,
      },
    ],
  });
}

async function handleRemoveBlock(
  event: RemoveBlockEvent,
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: LayoutStep
): Promise<void> {
  assertPipelineStepConfigNonNull(layoutStep);
  const updatedConfig = replaceBlockInConfig(layoutStep.config, event.layoutPath, {
    type: 'PLACEHOLDER',
  });
  const remainingEdges = filterEdgesToRemainingRefs(layoutStep.incomingEdges, updatedConfig.sections);

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [
      {
        stepId: layoutStep.id,
        incomingEdges: remainingEdges,
        config: updatedConfig as Record<string, JSONValue>,
        rerunAfterUpdate: true,
      },
    ],
  });
}

async function handleMoveBlock(
  event: MoveBlockEvent,
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: LayoutStep
): Promise<void> {
  assertPipelineStepConfigNonNull(layoutStep);
  const gridSection = getAtLayoutPath<GridSectionType>(layoutStep.config, event.layoutPath);
  const {newRows, layoutChanged} = swapBlocks(gridSection.rows, event.source, event.target);
  if (!layoutChanged) {
    return;
  }
  const updatedConfig = produce(layoutStep.config, (draft) => {
    setAtLayoutPath(draft, [...event.layoutPath, 'rows'], newRows);
  });

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [{stepId: layoutStep.id, config: updatedConfig as Record<string, JSONValue>}],
  });
}

function ReadyAnalysisDataLayoutAdapter({
  analysis,
  analysisClientStateId,
  clientStateId,
  layoutStep,
  onChangeSelectedItem,
  onChangeSelectedRightSidebarItem,
}: AnalysisDataLayoutAdapterProps & {layoutStep: LayoutStep}): JSX.Element {
  const {selectedRightSidebarItem} = useRightSidebarItem(clientStateId);

  const {handleOnEvents, layout} = useHandleAnalysisLayoutEvents({
    analysis,
    clientStateId,
    layoutStep,
  });
  const isLayoutCreationEnabled = useIsAnalysisLayoutCreationEnabled();

  const overflowMenuOptions = getLayoutOverflowMenuOptions({
    analysis,
    analysisClientStateId,
    layoutStep,
    layout,
    isLayoutCreationEnabled,
  });

  assertPipelineStepConfigNonNull(layoutStep);
  const canEditLayout = !isLockedLayout(layoutStep.config) && !isAnalysisReadOnly(analysis);
  // Prevent concurrent add-block calls. getNextInputDataIndex uses raw datas length,
  // which only updates after the layout step re-runs. Without this lock, two rapid adds
  // would read the same length and produce duplicate toStepDataIndex values.
  const isAddingBlockRef = useRef(false);
  // Prevent concurrent remove-block calls. Each remove reads from layoutStep.config,
  // which only updates after the step re-runs. Without this lock, two rapid removes
  // would compute from stale config and the later one could overwrite the earlier.
  const isRemovingBlockRef = useRef(false);
  // Prevent concurrent move-block calls. Each move reads from layoutStep.config,
  // which only updates after the step re-runs. Without this lock, two rapid moves
  // would compute from stale config and the later one could overwrite the earlier.
  const isMovingBlockRef = useRef(false);

  const additionalBlockActions = useMemo(
    () =>
      getAdditionalBlockActions(
        analysis,
        layout,
        canEditLayout,
        onChangeSelectedItem,
        onChangeSelectedRightSidebarItem,
        selectedRightSidebarItem
      ),
    [
      analysis,
      canEditLayout,
      layout,
      onChangeSelectedItem,
      onChangeSelectedRightSidebarItem,
      selectedRightSidebarItem,
    ]
  );

  const handleAllEvents = useCallback(
    (events: Array<LayoutEvent>) => {
      const addBlockEvents = events.filter((e): e is AddBlockEvent => e.type === 'ADD_BLOCK');
      const removeBlockEvents = events.filter((e): e is RemoveBlockEvent => e.type === 'REMOVE_BLOCK');
      const configEvents = events.filter((e): e is ConfigFormFieldEvent => e.type === 'CONFIG_FORM_FIELD');
      const moveBlockEvents = events.filter((e): e is MoveBlockEvent => e.type === 'MOVE_BLOCK');

      if (configEvents.length > 0) {
        handleOnEvents(configEvents);
      }

      const nextAddBlock = addBlockEvents[0];
      if (nextAddBlock != null && !isAddingBlockRef.current) {
        isAddingBlockRef.current = true;
        handleAddBlock(nextAddBlock, analysis, layoutStep)
          .catch(() => {
            pushToast({
              type: ToastType.DANGER,
              message: 'Something went wrong while adding the view. Please try again.',
            });
          })
          .finally(() => {
            isAddingBlockRef.current = false;
          });
      }

      for (const event of removeBlockEvents) {
        if (isRemovingBlockRef.current) {
          continue;
        }
        isRemovingBlockRef.current = true;
        handleRemoveBlock(event, analysis, layoutStep)
          .catch(() => {
            pushToast({
              type: ToastType.DANGER,
              message: 'Something went wrong while removing the view. Please try again.',
            });
          })
          .finally(() => {
            isRemovingBlockRef.current = false;
          });
      }

      for (const event of moveBlockEvents) {
        if (isMovingBlockRef.current) {
          continue;
        }
        isMovingBlockRef.current = true;
        handleMoveBlock(event, analysis, layoutStep)
          .catch(() => {
            pushToast({
              type: ToastType.DANGER,
              message: 'Something went wrong while moving the block. Please try again.',
            });
          })
          .finally(() => {
            isMovingBlockRef.current = false;
          });
      }
    },
    [analysis, handleOnEvents, layoutStep]
  );

  // TODO BNCH-146595: Use step type ancestry or a dedicated config field instead of matching on layout name.
  const isEOLayout = layout.name === 'Model details' || layout.name === 'Recommendations';

  return (
    <DataLayout
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      additionalTitleComponents={
        isEOLayout ? (
          <ExperimentOptimizationFeedbackToolbar
            clientStateId={clientStateId}
            feedbackContext={{analysisId: analysis.id, layoutStepId: layoutStep.id}}
          />
        ) : undefined
      }
      additionalBlockActions={additionalBlockActions}
      clientId={layoutStep.id}
      isEditable={canEditLayout}
      layout={layout}
      onEvents={handleAllEvents}
      overflowMenuOptions={overflowMenuOptions}
      steps={analysis.steps}
    />
  );
}

function getAdditionalBlockActions(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layout: DataLayoutType,
  canEditLayout: boolean,
  onChangeSelectedItem: AnalysisDataLayoutAdapterProps['onChangeSelectedItem'],
  onChangeSelectedRightSidebarItem: AnalysisDataLayoutAdapterProps['onChangeSelectedRightSidebarItem'],
  selectedRightSidebarItem: AnalysisRightSidebarItem | null
): Record<string, AdditionalBlockActions> {
  if (!canEditLayout) {
    return {};
  }
  const result: Record<string, AdditionalBlockActions> = {};

  function collectBlockActions(rows: Array<Array<Block>>): void {
    for (const row of rows) {
      for (const block of row) {
        if (block.type !== 'DATA') {
          continue;
        }
        const editAction = getEditViewAction(
          analysis,
          block.view,
          onChangeSelectedItem,
          onChangeSelectedRightSidebarItem,
          selectedRightSidebarItem
        );
        const buttonActions: Array<ButtonDataViewActionInterface> = [];
        if (editAction != null && isDataViewActionConstantType(editAction)) {
          buttonActions.push(editAction);
        }
        result[getDefaultClientIdForView(block.view)] = {buttonActions};
      }
    }
  }

  for (const section of layout.sections) {
    const rows = section.type === 'GRID' ? section.rows : section.tabs.flatMap((tab) => tab.rows);
    collectBlockActions(rows);
  }
  return result;
}
